import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, { useCallback, useMemo, useState } from 'react';

import { DragOverlayCell } from './DragOverlayCell';
import { ColumnDef, SortModel } from './ServiceTable.types';
import { SortableHeaderCell } from './SortableHeaderCell';

export interface ServiceTableHeadProps {
  columns: ColumnDef[];
  sortModel: SortModel;
  onSort: (field: string) => void;
  allSelected: boolean;
  someSelected: boolean;
  onSelectAll: (checked: boolean) => void;
  onColumnReorder?: (oldIndex: number, newIndex: number) => void;
  /** When provided, each header cell gets a drag-to-resize handle that commits
   *  new widths via this callback. */
  onColumnResize?: (key: string, width: number, seed?: Record<string, number>) => void;
  /** When the table is in its frozen fixed-pixel model, the last data column
   *  is rendered elastic so it absorbs the slack between the frozen width and
   *  the container. Its width is therefore not its own to set, so its
   *  drag-to-resize handle is suppressed rather than left as a dead control. */
  elasticLastColumn?: boolean;
  /** When true, omit the Select-all checkbox cell and the trailing Actions
   *  cell so the header matches client/customer rows that drop the same
   *  columns. */
  isClientCustomer?: boolean;
}

const STICKY_CELL_SX = {
  fontSize: '10px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  fontWeight: 600,
  color: 'text.secondary',
  cursor: 'default',
  whiteSpace: 'nowrap' as const,
  py: 0.75,
  px: 1,
  borderBottom: '2px solid',
  borderColor: 'v4.border',
  width: 70,
  position: 'sticky' as const,
  right: 0,
  bgcolor: 'v4.tableEven',
  zIndex: 2,
} as const;

export const ServiceTableHead: React.FC<ServiceTableHeadProps> = ({
  columns,
  sortModel,
  onSort,
  allSelected,
  someSelected,
  onSelectAll,
  onColumnReorder,
  onColumnResize,
  elasticLastColumn = false,
  isClientCustomer = false,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const columnIds = useMemo(() => columns.map((c) => c.key), [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = useCallback((e: DragStartEvent) => {
    setActiveId(String(e.active.id));
  }, []);

  const handleDragOver = useCallback((e: DragOverEvent) => {
    setOverId(e.over ? String(e.over.id) : null);
  }, []);

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      setActiveId(null);
      setOverId(null);
      const { active, over } = e;
      if (!over || active.id === over.id || !onColumnReorder) return;
      const oldIndex = columns.findIndex((c) => c.key === active.id);
      const newIndex = columns.findIndex((c) => c.key === over.id);
      if (oldIndex !== -1 && newIndex !== -1) onColumnReorder(oldIndex, newIndex);
    },
    [columns, onColumnReorder]
  );

  const activeCol = activeId ? columns.find((c) => c.key === activeId) : null;

  // Determine which side of the over-target the indicator should appear
  const dropSide = useMemo<'left' | 'right' | null>(() => {
    if (!activeId || !overId || activeId === overId) return null;
    const activeIdx = columns.findIndex((c) => c.key === activeId);
    const overIdx = columns.findIndex((c) => c.key === overId);
    return activeIdx < overIdx ? 'right' : 'left';
  }, [activeId, overId, columns]);

  return (
    <TableHead>
      <TableRow sx={{ bgcolor: 'v4.tableEven' }}>
        {!isClientCustomer && (
          <TableCell
            padding="checkbox"
            sx={{
              ...STICKY_CELL_SX,
              // Match the body checkbox cell + the frozen colgroup width (48) so
              // nothing shifts when the table freezes to fixed layout on resize.
              width: 48,
              minWidth: 48,
              maxWidth: 48,
              position: 'static',
              boxShadow: 'none',
            }}
          >
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={(e) => onSelectAll(e.target.checked)}
              slotProps={{ input: { 'aria-label': 'Select all rows' } }}
            />
          </TableCell>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          accessibility={{ container: document.body }}
        >
          <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
            {columns.map((col, colIndex) => (
              <SortableHeaderCell
                key={col.key}
                col={col}
                sortModel={sortModel}
                onSort={onSort}
                dropIndicator={col.key === overId ? dropSide : null}
                onColumnResize={
                  elasticLastColumn && colIndex === columns.length - 1 ? undefined : onColumnResize
                }
              />
            ))}
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {activeCol ? <DragOverlayCell label={activeCol.label} /> : null}
          </DragOverlay>
        </DndContext>
        {!isClientCustomer && <TableCell sx={STICKY_CELL_SX} aria-label="Actions" />}
      </TableRow>
    </TableHead>
  );
};
